import { create } from "zustand";
import { persist } from "zustand/middleware";

// How many recent spaces the sidebar renders inline before the
// "Показать все" overflow link. Lives here so the store can use it to
// decide whether a clicked space is currently visible.
export const RECENT_SPACES_VISIBLE = 7;

// MRU buffer is larger than the visible limit so the list keeps rotating
// naturally as the user visits 4th/5th spaces — only the top N are
// rendered.
const RECENT_BUFFER = 10;

interface SpaceStoreState {
  /** Active space id sent as `X-Space-Id` on every API call. `null` means
   *  the backend resolves the user's first/default space (lazy-creates a
   *  personal one if needed). Persisted across reloads. */
  currentSpaceId: string | null;
  /** Space ids in most-recently-used order. Updated whenever the user
   *  switches to a non-null space. Persisted across reloads. */
  recentSpaceIds: string[];
  setCurrentSpaceId: (id: string | null) => void;
  /** Seed the MRU once at boot from the server's space order. No-op if
   *  MRU is already populated — preserves the user's stable slot order
   *  across reloads. */
  seedRecentSpaceIds: (ids: string[]) => void;
  reset: () => void;
}

export const useSpaceStore = create<SpaceStoreState>()(
  persist(
    (set) => ({
      currentSpaceId: null,
      recentSpaceIds: [],
      setCurrentSpaceId: (id) =>
        set((state) => {
          if (!id) return { currentSpaceId: id };
          // Stable visible slots: if the space is already in the
          // currently-rendered top-N, leave the order alone — clicking
          // between visible spaces must not reshuffle. If the space is
          // off-screen (new entirely, OR below position N in MRU),
          // promote it to the top so the user actually sees it appear
          // in the sidebar.
          const idx = state.recentSpaceIds.indexOf(id);
          if (idx !== -1 && idx < RECENT_SPACES_VISIBLE) {
            return { currentSpaceId: id };
          }
          const next = [id, ...state.recentSpaceIds.filter((x) => x !== id)].slice(
            0,
            RECENT_BUFFER,
          );
          return { currentSpaceId: id, recentSpaceIds: next };
        }),
      seedRecentSpaceIds: (ids) =>
        set((state) => {
          if (state.recentSpaceIds.length > 0) return state;
          return { recentSpaceIds: ids.slice(0, RECENT_BUFFER) };
        }),
      reset: () => set({ currentSpaceId: null, recentSpaceIds: [] }),
    }),
    { name: "strands.currentSpace" },
  ),
);
